import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { formatDisplayName } from "@/shared/utils/formatters";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { profileService } from "@/features/profile/services/visaService";
import { companyService } from "@/features/company/services/companyService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";

const OFFICER_TYPES = ["officer", "admin"];

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

const GENDER_OPTIONS = [
  { value: "Male", key: "gender_male" },
  { value: "Female", key: "gender_female" },
];

interface ProfileFormData {
  first_name: string;
  middle_name: string;
  last_name: string;
  user_type: string;
  position: string;
  company: string;
  batch_no: string;
  company_branch: string;
  phone_number: string;
  postal_code: string;
  street_address: string;
  city: string;
  state_province: string;
  country: string;
  sending_org: string;
  emergency_contact_name: string;
  emergency_contact_number: string;
  emergency_contact_address: string;
  emergency_email: string;
  birthdate: string;
  gender: string;
  company_joining_date: string;
}

const emptyForm: ProfileFormData = {
  first_name: "",
  middle_name: "",
  last_name: "",
  user_type: "",
  position: "",
  company: "",
  batch_no: "",
  company_branch: "",
  phone_number: "",
  postal_code: "",
  street_address: "",
  city: "",
  state_province: "",
  country: "",
  sending_org: "",
  emergency_contact_name: "",
  emergency_contact_number: "",
  emergency_contact_address: "",
  emergency_email: "",
  birthdate: "",
  gender: "",
  company_joining_date: "",
};

export const PersonalInfoPage = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { userId: paramUserId } = useParams<{ userId: string }>();

  const { canWrite } = usePermissions();
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );
  const targetUserId = paramUserId || String(userData?.id || "");
  const isViewingOther = !!paramUserId && !!userData && paramUserId !== String(userData.id);

  const [form, setForm] = useState<ProfileFormData>(emptyForm);
  // Field visibility is based on the profile being viewed, not the logged-in user
  const isOfficerProfile = OFFICER_TYPES.includes(form.user_type?.toLowerCase() || "");
  const [companyNameText, setCompanyNameText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  // Dropdowns
  const [companies, setCompanies] = useState<
    { value: string; label: string }[]
  >([]);
  const [sendingOrgs, setSendingOrgs] = useState<
    { value: string; label: string }[]
  >([]);

  // Load profile data
  useEffect(() => {
    if (!targetUserId) return;
    setLoading(true);
    profileService
      .getUserProfile(targetUserId)
      .then((data: any) => {
        setForm({
          first_name: data.first_name || "",
          middle_name: data.middle_name || "",
          last_name: data.last_name || "",
          user_type: data.user_type || "",
          position: data.position || "",
          company: data.company || "",
          batch_no: data.batch_no || "",
          company_branch: data.company_branch || "",
          phone_number: data.phone_number || "",
          postal_code: data.postal_code || "",
          street_address: data.street_address || "",
          city: data.city || "",
          state_province: data.state_province || "",
          country: data.country || "",
          sending_org: data.sending_org || "",
          emergency_contact_name: data.emergency_contact_name || "",
          emergency_contact_number: data.emergency_contact_number || "",
          emergency_contact_address: data.emergency_contact_address || "",
          emergency_email: data.emergency_email || "",
          birthdate: data.birthdate ? data.birthdate.slice(0, 10) : "",
          gender: data.gender || "",
          company_joining_date: data.company_joining_date
            ? data.company_joining_date.slice(0, 10)
            : "",
        });
        setCompanyNameText(data.company_name_text || "");
      })
      .catch(() => showToast(t("visa.fetch_error"), "error"))
      .finally(() => setLoading(false));
  }, [targetUserId, t]);

  // Load company dropdown
  useEffect(() => {
    companyService.getDropdown().then(setCompanies).catch(console.error);
  }, []);

  // Load sending orgs when country changes
  useEffect(() => {
    if (form.country) {
      sendingOrgService
        .getDropdown(form.country)
        .then((data: any[]) => {
          const normalized = data.map((o: any) => ({
            value: o.value || o.id,
            label: o.label || o.name,
          }));
          setSendingOrgs(normalized);
        })
        .catch(() => setSendingOrgs([]));
    } else {
      setSendingOrgs([]);
    }
  }, [form.country]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "country") {
      setForm((prev) => ({ ...prev, sending_org: "" }));
    }
  };

  const handleSave = async () => {
    // Basic validation for required fields
    if (!form.first_name || !form.last_name || (!isOfficerProfile && !form.country)) {
      showToast(t("auth.error_fill_fields"), "error");
      return;
    }

    setSaving(true);
    try {
      await profileService.updateUserProfile(targetUserId, form);
      showToast(t("profile.update_success"), "success");
    } catch {
      showToast(t("profile.update_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/users"
              className="p-2 -ml-2 text-gray-400 hover:text-gray-600 active:text-gray-800 transition-colors rounded-lg shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </Link>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
                {t("settings.personal_info")}
              </h1>
              {isViewingOther && (
                <p className="text-xs text-indigo-600 mt-0.5 truncate">
                  {t("settings.viewing_user")}: {formatDisplayName(form.last_name, form.first_name, form.middle_name)}
                </p>
              )}
            </div>
          </div>
          {(canWrite("profile") || !isViewingOther) && (
            <Button onClick={handleSave} loading={saving} className="shrink-0">
              {t("common.save")}
            </Button>
          )}
        </div>
      </div>

      {/* Personal Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
          {t("profile.section_personal")}
        </h2>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label={t("auth.first_name_label")}
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
            />
            <Input
              label={t("profile.middle_name")}
              name="middle_name"
              value={form.middle_name}
              onChange={handleChange}
            />
            <Input
              label={t("auth.last_name_label")}
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
            />
          </div>
          {!isOfficerProfile && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label={t("profile.birthdate")}
                  name="birthdate"
                  type="date"
                  value={form.birthdate}
                  onChange={handleChange}
                />
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("profile.gender")}
                  </label>
                  <select
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t("profile.select_gender")}</option>
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {t(`profile.${g.key}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t("profile.country")}
                </label>
                <select
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">{t("profile.select_country")}</option>
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {t(`profile.${c.key}`)}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Work Assignment, Contact & Address, Emergency Contact — hidden when viewed profile is officer type */}
      {!isOfficerProfile && (
        <>
          {/* Work Assignment */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              {t("profile.section_work")}
            </h2>
            <div className="space-y-3">
              {isOfficer ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("profile.user_type")}
                    </label>
                    <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      {form.user_type || "-"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("profile.company")}
                    </label>
                    <select
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">{t("common.select")}</option>
                      {companies.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("profile.user_type")}
                    </label>
                    <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      {form.user_type || "-"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("profile.company")}
                    </label>
                    <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      {companies.find((c) => c.value === form.company)?.label || companyNameText || "-"}
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("profile.batch_no")}
                  </label>
                  <input
                    type="text"
                    name="batch_no"
                    value={form.batch_no}
                    onChange={handleChange}
                    placeholder={t("profile.batch_no_placeholder")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <Input
                  label={t("profile.position")}
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* <Input
                  label={t("profile.company_branch")}
                  name="company_branch"
                  value={form.company_branch}
                  onChange={handleChange}
                /> */}
                <Input
                  label={t("profile.company_joining_date")}
                  name="company_joining_date"
                  type="date"
                  value={form.company_joining_date}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t("profile.sending_org")}
                </label>
                {form.country ? (
                  <select
                    name="sending_org"
                    value={form.sending_org}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t("profile.select_sending_org")}</option>
                    {sendingOrgs.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-400 italic">
                    {t("profile.sending_org_select_country_first")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact & Address */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              {t("profile.section_contact_address")}
            </h2>
            <div className="space-y-3">
              <Input
                label={t("auth.phone_label")}
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
              />
              <Input
                label={t("profile.street_address")}
                name="street_address"
                value={form.street_address}
                onChange={handleChange}
              />
              <div className="grid grid-cols-3 gap-3">
                <Input
                  label={t("profile.city")}
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                />
                <Input
                  label={t("profile.state_province")}
                  name="state_province"
                  value={form.state_province}
                  onChange={handleChange}
                />
                <Input
                  label={t("profile.zip_code")}
                  name="postal_code"
                  value={form.postal_code}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              {t("profile.section_emergency")}
            </h2>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label={t("profile.emergency_contact_name")}
                  name="emergency_contact_name"
                  value={form.emergency_contact_name}
                  onChange={handleChange}
                />
                <Input
                  label={t("profile.emergency_contact_number")}
                  name="emergency_contact_number"
                  value={form.emergency_contact_number}
                  onChange={handleChange}
                />
              </div>
              <Input
                label={t("profile.emergency_contact_address")}
                name="emergency_contact_address"
                value={form.emergency_contact_address}
                onChange={handleChange}
              />
              <Input
                label={t("profile.emergency_email")}
                name="emergency_email"
                type="email"
                value={form.emergency_email}
                onChange={handleChange}
              />
            </div>
          </div>
        </>
      )}

      {/* Footer note */}
      <p className="text-xs text-gray-400 italic text-center mb-8">
        {t("profile.footer_note")}
      </p>
    </div>
  );
};
